/**
 * Share a studio sound — mirrors the slides/report pattern:
 *  - "Who can see this": Only me · Team members (pick people) · All team  (VisibilityControl)
 *  - "Anyone with the link can view" + copy / reset link                  (ShareLinkControl)
 * The cowork controls are wired to the cowork API, so this dialog re-implements them against the audio API
 * (POST /track/{id}/update, GET /team/members, POST /track/{id}/share). Owner-only; others see the current setting.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, ExternalLink, Globe, Loader2, Lock, RefreshCw, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/components/ui/utils';
import { audioStudioApi, soundShareUrl, type StudioTrack, type Visibility } from '@/api/audioStudioApi';
import { audioKeys, useTeamMembers } from './queries';

const OPTIONS: Array<{ id: Visibility; label: string; hint: string; Icon: typeof Lock }> = [
  { id: 'private', label: 'Only me', hint: 'Nobody else on the team sees it.', Icon: Lock },
  { id: 'members', label: 'Team members', hint: 'Pick who can see it.', Icon: Users },
  { id: 'team', label: 'All team', hint: 'Everyone on the team.', Icon: Globe },
];

export function AudioShareDialog({ track, open, onOpenChange, onUpdated }: { track: StudioTrack | null; open: boolean; onOpenChange: (o: boolean) => void; onUpdated?: (t: StudioTrack) => void }) {
  const qc = useQueryClient();
  const canEdit = !!track?.is_owner;
  const [visibility, setVisibility] = useState<Visibility>('team');
  const [allowed, setAllowed] = useState<string[]>([]);
  const [filter, setFilter] = useState('');
  const [share, setShare] = useState<{ enabled: boolean; token: string | null }>({ enabled: false, token: null });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !track) return;
    setVisibility(track.visibility || 'team');
    setAllowed(track.allowed_user_ids || []);
    setShare({ enabled: track.share_enabled, token: track.share_token });
    setFilter('');
  }, [open, track]);

  const members = useTeamMembers(open && visibility === 'members');
  const list = (members.data?.members || []).filter(
    (m) => m.user_id !== members.data?.me && (!filter.trim() || `${m.name} ${m.email || ''}`.toLowerCase().includes(filter.trim().toLowerCase())),
  );

  const refresh = (t?: StudioTrack) => {
    void qc.invalidateQueries({ queryKey: audioKeys.tracksAll });
    if (t) onUpdated?.(t);
  };

  const saveVisibility = useMutation({
    mutationFn: () => audioStudioApi.updateTrack(track!.track_id, { visibility, allowed_user_ids: visibility === 'members' ? allowed : [] }),
    onSuccess: (t) => { toast.success('Sharing updated'); refresh(t); },
    onError: (e: Error) => toast.error(e.message),
  });

  const setLink = useMutation({
    mutationFn: (body: { enabled?: boolean; rotate?: boolean }) => audioStudioApi.shareTrack(track!.track_id, body),
    onSuccess: (r, body) => {
      setShare({ enabled: r.share_enabled, token: r.share_token });
      toast.success(body.rotate ? 'New link created — the old one no longer works' : r.share_enabled ? 'Anyone with the link can listen' : 'Link sharing is off');
      if (track) refresh({ ...track, share_enabled: r.share_enabled, share_token: r.share_token });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const publicUrl = track && share.enabled && share.token ? soundShareUrl(track.track_id, share.token) : '';
  const copy = async () => {
    if (!publicUrl) return;
    try { await navigator.clipboard.writeText(publicUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { toast.error('Could not copy — select the link and copy it'); }
  };
  const dirty = !!track && (visibility !== track.visibility || (visibility === 'members' && allowed.slice().sort().join(',') !== (track.allowed_user_ids || []).slice().sort().join(',')));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share “{track?.title || 'sound'}”</DialogTitle>
          <DialogDescription>{canEdit ? 'Choose who on your team can find it, or share a public link.' : 'Only the person who created this sound can change who sees it.'}</DialogDescription>
        </DialogHeader>

        <section aria-label="Who can see this">
          <div className="mb-1.5 text-[12px] text-[#344054]" style={{ fontWeight: 600 }}>Who can see this</div>
          <div className="space-y-1" role="radiogroup">
            {OPTIONS.map((o) => (
              <button
                key={o.id}
                type="button"
                role="radio"
                aria-checked={visibility === o.id}
                disabled={!canEdit}
                onClick={() => setVisibility(o.id)}
                className={cn('flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left disabled:cursor-default', visibility === o.id ? 'bg-[#f4ecff]' : 'hover:bg-[#f9fafb]')}
              >
                <o.Icon className={cn('mt-0.5 h-4 w-4', visibility === o.id ? 'text-[#7c3aed]' : 'text-[#667085]')} aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="block text-[12.5px] text-[#101828]" style={{ fontWeight: 600 }}>{o.label}</span>
                  <span className="block text-[11px] text-[#667085]">{o.hint}</span>
                </span>
                {visibility === o.id && <Check className="h-4 w-4 text-[#7c3aed]" aria-hidden />}
              </button>
            ))}
          </div>
          {visibility === 'members' && canEdit && (
            <div className="mt-2 rounded-lg border border-[#eaecf0] p-2">
              <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Find a team member…" aria-label="Find a team member" className="mb-1.5 w-full rounded-md border border-[#d0d5dd] px-2 py-1 text-[12px]" />
              <div className="max-h-44 overflow-y-auto">
                {members.isPending && <div className="flex items-center gap-2 px-2 py-2 text-[11.5px] text-[#98a2b3]"><Loader2 className="h-3 w-3 animate-spin" aria-hidden /> Loading team…</div>}
                {members.error && <div className="px-2 py-2 text-[11.5px] text-[#b42318]">Could not load the team: {(members.error as Error).message}</div>}
                {list.map((m) => (
                  <label key={m.user_id} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 hover:bg-[#f9fafb]">
                    <input type="checkbox" checked={allowed.includes(m.user_id)} onChange={() => setAllowed((a) => (a.includes(m.user_id) ? a.filter((x) => x !== m.user_id) : [...a, m.user_id]))} className="accent-[#7c3aed]" />
                    <span className="min-w-0 flex-1 truncate text-[12px] text-[#101828]">{m.name}</span>
                    {m.role && <span className="text-[10px] uppercase tracking-wide text-[#98a2b3]">{m.role.replace(/_/g, ' ').toLowerCase()}</span>}
                  </label>
                ))}
                {members.data && !list.length && <div className="px-2 py-2 text-[11.5px] text-[#98a2b3]">No one matches.</div>}
              </div>
              {!allowed.length && <div className="px-2 pt-1 text-[11px] text-[#b54708]">Pick at least one member.</div>}
            </div>
          )}
          {canEdit && (
            <div className="mt-2 flex justify-end">
              <Button size="sm" disabled={!dirty || saveVisibility.isPending || (visibility === 'members' && !allowed.length)} onClick={() => saveVisibility.mutate()} className="bg-[#be43ff] text-white hover:bg-[#a835e0]">
                {saveVisibility.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />} Save
              </Button>
            </div>
          )}
        </section>

        <section aria-label="Share link" className="border-t border-[#eaecf0] pt-3">
          <label className={cn('flex items-start gap-2.5', canEdit ? 'cursor-pointer' : 'cursor-default opacity-80')}>
            <input type="checkbox" checked={share.enabled} disabled={!canEdit || setLink.isPending} onChange={(e) => setLink.mutate({ enabled: e.target.checked })} className="mt-0.5 accent-[#7c3aed]" />
            <span className="min-w-0 flex-1">
              <span className="block text-[12.5px] text-[#101828]" style={{ fontWeight: 600 }}>Anyone with the link can listen</span>
              <span className="block text-[11px] text-[#667085]">A public, read-only player — no login. Turn it off any time.</span>
            </span>
            {setLink.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin text-[#98a2b3]" aria-hidden />}
          </label>
          {publicUrl && (
            <>
              <div className="mt-2 flex items-center gap-1.5">
                <input readOnly value={publicUrl} aria-label="Share link" onFocus={(e) => e.currentTarget.select()} className="min-w-0 flex-1 rounded-md border border-[#d0d5dd] bg-[#f9fafb] px-2 py-1 text-[11.5px] text-[#344054]" />
                <button type="button" onClick={copy} className="flex items-center gap-1 rounded-md bg-[#be43ff] px-2.5 py-1 text-[12px] font-semibold text-white hover:bg-[#a835e0]">
                  {copied ? <Check className="h-3.5 w-3.5" aria-hidden /> : <Copy className="h-3.5 w-3.5" aria-hidden />} {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <div className="mt-2 flex items-center justify-between text-[11.5px]">
                <a href={publicUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[#7c3aed] hover:underline"><ExternalLink className="h-3 w-3" aria-hidden /> Open the public player</a>
                {canEdit && (
                  <button type="button" onClick={() => setLink.mutate({ enabled: true, rotate: true })} disabled={setLink.isPending} className="flex items-center gap-1 text-[#667085] hover:text-[#101828]">
                    <RefreshCw className="h-3 w-3" aria-hidden /> Reset link
                  </button>
                )}
              </div>
            </>
          )}
        </section>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
